import dayjs from "dayjs";
import _ from "lodash";
import ReactDOMServer from "react-dom/server";
import styled from "styled-components";

import { algoChangeDateConfig } from "@similarweb/pro-logic-components";
import { colorsSets, colorsPalettes, fonts } from "@similarweb/styles";
import { ChangeTooltip } from "@similarweb/ui-components";

import { tickIntervals } from "app/components/widget/widget-types/GraphWidget";
import { i18nFilter, translate } from "app/filters/i18nFilter";
import { percentageSignFilter } from "app/filters/ngFilters";
import { CustomBanner } from "app/pages/segments/analysis/components/CustomBanner";
import { getAlgoChangeDataTimestamp } from "app/pages/segments/analysis/utils/algoChangeData";
import { getChangeColor } from "app/pages/website-analysis/TrafficAndEngagement/Metrics/BounceRate";
import { granularityConfigs } from "app/pages/website-analysis/website-content/leading-folders/FolderAnalysisDefaults";
import DurationService from "app/services/DurationService";
import type { ICustomSegment, ICustomSegmentGroupWebsite } from "app/services/segments/segmentsApiService";
import { SEGMENT_TYPES } from "app/services/segments/segmentsApiService";
import defaultTooltip from "pro-features/components/Chart/src/configs/tooltip/defaultTooltip";
import tooltipContainerConfig from "pro-features/components/Chart/src/configs/tooltip/tooltipContainerConfig";
import { formatTooltipPointWithConfidence } from "pro-features/components/Chart/src/data/confidenceProcessor";

import combineConfigs from "../../../../components/Chart/src/combineConfigs";
import noLegendConfig from "../../../../components/Chart/src/configs/legend/noLegendConfig";
import markerWithDashedConfig from "../../../../components/Chart/src/configs/series/markerWithDashedLinePerPointChartConfig";
import xAxisCrosshair from "../../../../components/Chart/src/configs/xAxis/xAxisCrosshair";
import xAxisLabelsConfig from "../../../../components/Chart/src/configs/xAxis/xAxisLabelsConfig";
import yAxisLabelsConfig from "../../../../components/Chart/src/configs/yAxis/yAxisLabelsConfig";

import type { ICustomSegmentAvailableMembers } from "app/services/segments/SegmentsUtils";
import { SegmentsUtils } from "app/services/segments/SegmentsUtils";

export const SegmentTypeBadge = styled.span`
    display: inline;
    width: 38px;
    padding: 4px 7px 4px;
    margin-left: 11px;
    color: ${colorsPalettes.carbon[500]};
    background: ${colorsPalettes.carbon[50]};
    border-radius: 8px;
    font-size: 9px;
    line-height: 8px;
    text-transform: uppercase;
    font-family: ${fonts.$robotoFontFamily};
`;

export const SegmentSubtitle = styled.span`
    color: ${colorsPalettes.carbon[300]};
    vertical-align: middle;
    margin-left: 11px;
    line-height: 16px;
    font-size: 12px;
    max-width: 120px;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
    font-family: ${fonts.$robotoFontFamily};
`;

const getChangeTooltip = ({
    filter,
    granularity = "Monthly",
    metric,
    isWindow = false,
    toDateMoment = undefined,
    rowHeight,
    data = undefined,
    showChangeColumn,
    changeTooltipHeaders = [
        { position: 0, displayName: "Segment" },
        {
            position: 1,
            displayName: translate(metric.chartTooltipTitle?.[granularity] || metric.title),
        },
        { position: 2, displayName: "Change" },
    ],
    invertTooltipColor,
    algoChangeDate = undefined,
}: FixmeType) => {
    return {
        tooltip: {
            shared: true,
            outside: false,
            useHTML: true,
            formatter(this: FixmeType): string {
                const getTooltipHeader = () => {
                    let date;
                    switch (granularity) {
                        case "Daily":
                            date = dayjs.utc(this.x).format("dddd, MMM DD, YYYY");
                            break;
                        case "Weekly":
                            const to: any = dayjs.utc(_.last(this?.points?.series?.xData));
                            const from: any = dayjs.utc(this.x);
                            const isLast: boolean = _.last(this?.points?.[0]?.series?.xData) === this.x;
                            let toWeek = dayjs.utc(this.x).add(6, "days");
                            // show partial week in case of last point when start of week and end of week aren't in the same month.
                            if (isLast && !isWindow) {
                                if (from.month() !== toWeek.month()) {
                                    toWeek = from.clone().endOf("month").startOf("day").utc();
                                }
                            } else if (isLast && isWindow && toDateMoment) {
                                toWeek = toDateMoment;
                            }
                            date = `From ${dayjs.utc(this.x).format("MMM DD, YYYY")} to ${toWeek.format(
                                "MMM DD, YYYY",
                            )}`;
                            break;
                        case "Monthly":
                            date = dayjs.utc(this.x).format("MMM. YYYY");
                            break;
                    }
                    return date;
                };

                const getChange = (previousDataPoint: FixmeType, change: FixmeType) =>
                    previousDataPoint
                        ? change !== 0 && percentageSignFilter()(change, 2)
                        : i18nFilter()("common.tooltip.change.new");

                const changeTooltipProp = () => {
                    // @ts-ignore FIXME: strict migration - 'data' possibly undefined — getChangeTooltip is only invoked from getChartConfig when data is truthy
                    return data.map((rowData: FixmeType) => {
                        const pointIndex = rowData.data.findIndex((p: FixmeType) => p.x === this.x);
                        const point = pointIndex > -1 ? rowData.data[pointIndex] : null;
                        const previousPoint =
                            pointIndex === 0 ? rowData.data[pointIndex] : rowData.data[pointIndex - 1];
                        const change = point && previousPoint ? point.y / previousPoint.y - 1 : null;
                        return {
                            displayName: rowData.name,
                            value: formatTooltipPointWithConfidence(point?.y, point?.confidence, filter),
                            color: rowData.color,
                            subtitle: rowData?.seriesSubtitle,
                            change: point?.y && previousPoint !== point && getChange(previousPoint.y, change),
                        };
                    });
                };

                const changeTooltipProps = {
                    rowHeight,
                    header: getTooltipHeader(),
                    tableHeaders: changeTooltipHeaders,
                    tableRows: changeTooltipProp(),
                    showChangeColumn,
                };

                if (invertTooltipColor) {
                    // @ts-ignore FIXME: strict migration - dynamic index assignment 'getChangeColor' on inferred object literal type
                    changeTooltipProps["getChangeColor"] = getChangeColor;
                }
                const shouldDisplayCustomTooltipBanner = algoChangeDate === this.x && metric.displayCustomAnnotation;
                return ReactDOMServer.renderToString(
                    <>
                        <ChangeTooltip {...changeTooltipProps} />
                        {shouldDisplayCustomTooltipBanner ? <CustomBanner /> : null}
                    </>,
                );
            },
        },
    };
};

export const getChartConfig = ({
    type,
    filter,
    data,
    metric,
    height = 400,
    timeGranularity = "Monthly",
    isWindow = false,
    toDateMoment = undefined,
    yAxisFilter = undefined,
    durationObject = undefined,
    showChangeColumn = true,
    changeTooltipHeaders = undefined,
    invertTooltipColor = false,
}: FixmeType) => {
    const { from, to } = durationObject.raw;
    const durationInMonths = DurationService.getMonthsFromApiDuration(from, to, isWindow);
    const format = durationInMonths <= 1 && timeGranularity !== "Monthly" ? "D MMM." : `MMM'YY`;
    // @ts-ignore FIXME: strict migration - dynamic string index into granularityConfigs whose type only declares Daily/Weekly/Monthly keys
    const currentGranularity = granularityConfigs[timeGranularity];
    const isTurboThreshold = timeGranularity === "Daily";
    const yAxisFormatter = ({ value }: FixmeType) =>
        yAxisFilter ? yAxisFilter[0]()(value, yAxisFilter[1]) : filter[0]()(value, filter[1]);
    const xAxisFormatter = ({ value }: FixmeType) => dayjs.utc(value).utc().format(format);
    const algoChangeDate = getAlgoChangeDataTimestamp();
    return combineConfigs(
        {
            type,
            yAxisFormatter,
            xAxisFormatter,
            granularity: timeGranularity,
            data: [],
        },
        [
            currentGranularity,
            noLegendConfig,
            yAxisLabelsConfig,
            xAxisLabelsConfig,
            xAxisCrosshair,
            tooltipContainerConfig,
            algoChangeDate && metric.displayCustomAnnotation && data
                ? algoChangeDateConfig(algoChangeDate, {
                      showTooltip: true,
                      description: translate("custom.segment.analysis.tooltip.unique.users.plotline"),
                      overwriteDateRange: true,
                      showDateTitle: false,
                  })
                : {},
            data
                ? getChangeTooltip({
                      filter,
                      metric,
                      granularity: timeGranularity,
                      isWindow: durationObject.forAPI?.isWindow,
                      toDateMoment: to,
                      rowHeight: data?.[0] && "seriesSubtitle" in data[0] ? 34 : 24,
                      data,
                      showChangeColumn,
                      changeTooltipHeaders,
                      invertTooltipColor,
                      algoChangeDate,
                  })
                : defaultTooltip,
            {
                chart: {
                    height,
                    type,
                    spacingTop: 10,
                    plotBackgroundColor: "transparent",
                    zooming: {
                        type: null,
                    },
                    events: {},
                },
                plotOptions: {
                    line: {
                        lineWidth: 2,
                        connectNulls: false,
                        animation: false,
                        marker: {
                            enabled: isMarkerEnabled(data),
                        },
                    },
                    area: {
                        stacking: "normal",
                        marker: {
                            enabled: false,
                        },
                    },
                    series: {
                        turboThreshold: isTurboThreshold ? 1500 : 0,
                    },
                },
                yAxis: {
                    gridLineWidth: 0.5,
                    showFirstLabel: true,
                    showLastLabel: true,
                    reversed: false,
                    gridZIndex: 2,
                    reversedStacks: true,
                    tickPixelInterval: 50,
                    labels: {
                        style: {
                            textTransform: "uppercase",
                            fontSize: "11px",
                            color: "#919191",
                        },
                    },
                },
                xAxis: {
                    gridLineWidth: 0,
                    gridLineDashStyle: "dash",
                    tickLength: 5,
                    labels: {
                        style: {
                            textTransform: "capitalize",
                            fontSize: "11px",
                            color: "#919191",
                        },
                    },
                    tickInterval: getXAxisTickInterval(durationObject, isWindow, timeGranularity),
                    minPadding: 0,
                    maxPadding: 0,
                },
            },
        ],
    );
};

export const dateToUTC = (dateString: string) => {
    const date = dateString.split("-");
    return Date.UTC(parseInt(date[0], 10), parseInt(date[1], 10) - 1, parseInt(date[2], 10));
};

export const transformData = (
    data: FixmeType,
    selectedRows: FixmeType,
    rowSelectionProp: string,
    availableMembers: ICustomSegmentAvailableMembers,
    timeGranularity?: string,
    durationObject?: any,
) => {
    return _.map(Object.keys(data), (chartKey: string, index) => {
        const chartData = data[chartKey];
        const [customSegment, customSegmentType] = SegmentsUtils.getSegmentObjectByKey(chartKey, availableMembers);
        const color = selectedRows
            ? _.result(
                  _.find(selectedRows, (row: any) => String(row[rowSelectionProp]) === String(chartKey)),
                  "selectionColor",
              )
            : colorsSets.c.toArray()[index];
        const currentRow = selectedRows?.find((row: FixmeType) => row.SegmentId === chartKey);
        const baseSeriesConfig: any = {
            name: chartKey,
            seriesSubtitle: "",
            color,
            rowIndex: currentRow.index,
            tooltipIndex: _.findIndex(selectedRows, (row: any) => String(row[rowSelectionProp]) === String(chartKey)),
        };
        switch (customSegmentType) {
            case SEGMENT_TYPES.SEGMENT:
                if (customSegment) {
                    const customSegmentObj = customSegment as ICustomSegment;
                    baseSeriesConfig.name = customSegmentObj.domain;
                    baseSeriesConfig.seriesSubtitle = <SegmentSubtitle>{customSegmentObj.segmentName}</SegmentSubtitle>;
                }
                break;
            case SEGMENT_TYPES.WEBSITE:
                if (customSegment) {
                    const customWebsiteObj = customSegment as ICustomSegmentGroupWebsite;
                    baseSeriesConfig.name = customWebsiteObj.domain;
                    baseSeriesConfig.seriesSubtitle = <SegmentTypeBadge>WEBSITE</SegmentTypeBadge>;
                }
                break;
        }
        return combineConfigs(
            {
                ...baseSeriesConfig,
                isDataSingleSeries: true,
                data: _.map(chartData, (dataPoint: any, dataPointIndex: number, dataPointArray: any[]) => {
                    if (!dataPoint) {
                        return;
                    }
                    const datekey = dataPoint.Key;
                    const confidenceLevel = dataPoint.Value?.Confidence;
                    let value = dataPoint.Value?.Value;
                    if (isNaN(parseFloat(value))) {
                        value = null;
                    }

                    // Check if this is a partial week at the end of the time range
                    let isPartialWeek = false;
                    const isLastPoint = dataPointIndex === dataPointArray.length - 1;

                    if (isLastPoint && timeGranularity === "Weekly" && durationObject?.raw?.to) {
                        const weekStart = dayjs.utc(datekey);
                        const weekEnd = weekStart.add(6, "days");
                        const durationEnd = durationObject.raw.to;

                        // Mark as partial if week extends beyond duration end or spans two months
                        isPartialWeek = weekEnd.isAfter(durationEnd) || weekStart.month() !== weekEnd.month();
                    }

                    const isPartial = (confidenceLevel >= 0.3 && confidenceLevel < 1) || isPartialWeek;

                    return [
                        dateToUTC(datekey),
                        value,
                        {
                            partial: isPartial,
                            hallowMarker: isPartial,
                            confidenceLevel,
                        },
                    ];
                }),
            },
            [baseSeriesConfig, markerWithDashedConfig],
        );
    });
};
const isMarkerEnabled = (data: any) => {
    return data?.[0]?.data?.length <= 24;
};

const getXAxisTickInterval = (durationObject: FixmeType, isWindow: boolean, timeGranularity: string) => {
    let ticks;
    const { from, to } = durationObject.raw;
    const durationInMonths = DurationService.getMonthsFromApiDuration(from, to, isWindow);
    if (durationInMonths > 24) {
        //every 2 months
        ticks = tickIntervals.monthly * 2;
    } else if (durationInMonths > 1) {
        ticks = tickIntervals.monthly;
    } else {
        switch (timeGranularity) {
            case "Daily":
            case "Weekly":
                ticks = tickIntervals.daily * 2;
                break;
            case "Monthly":
            default:
                //every other day
                ticks = tickIntervals.monthly;
                break;
        }
    }
    return ticks;
};
